/**
 * 1.4.0 asset groups, stream G1: the Library's group filter, sort and columns (step 0 mounted each).
 *
 *  - GroupFilterChip: at the top of the Filters pane while ?group= is set ("Series: American Horror Story ×"; the
 *    filter includes the sub-groups' members). f.group / f.setGroup (useLibraryFilters).
 *  - GroupSearchSuggestions: under the Library's search box, the groups whose titles match ("Series: …"); picking one
 *    calls onPick(groupId), which sets the filter. GET /api/v1/groups/suggest.
 *  - groupColumns: the columns menu's Series, Season and Episode no. (from AssetSummary.groups: the top of the
 *    asset's deepest group chain, the group of the Season type or the one below the top, and the asset's number).
 *  - GROUP_SORT_OPTION: the sort menu's group_order entry, offered only while a group filter is set (librarySort).
 */
import { Layers, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { AssetGroupRef, AssetSummary } from '@prism/contracts';
import { useGroup, useGroupSuggestions } from '../../../api/hooks/groups';
import { useDebounced } from '../../../lib/useDebounced';
import type { Column } from '../../LibraryColumns';
import type { LibraryFilterState } from '../useLibraryFilters';

export function GroupFilterChip({ f }: { f: LibraryFilterState }) {
  const g = useGroup(f.group);
  if (!f.group) return null;
  const label = g.data
    ? `${g.data.type.label}: ${[...g.data.path.map((p) => p.title), g.data.title].join(' › ')}`
    : g.isError ? 'A group that no longer exists' : 'Group…';
  return (
    <div className="group-filter" data-testid="group-filter">
      <span className="chip" title="Its members and the members of its sub-groups">
        <Layers size={12} />
        {g.data ? <Link to={`/groups/${f.group}`} className="group-filter-link">{label}</Link> : label}
        <button aria-label={`Remove filter ${label}`} onClick={() => f.setGroup(null)} data-testid="group-filter-clear"><X size={12} /></button>
      </span>
    </div>
  );
}

export function GroupSearchSuggestions({ q, onPick }: { q: string; onPick: (groupId: string) => void }) {
  const text = useDebounced(q, 200);
  const s = useGroupSuggestions(text);
  const items = q.trim().length >= 2 ? s.data?.items ?? [] : [];
  if (!items.length) return null;
  return (
    <ul className="group-suggest" role="listbox" aria-label="Series and seasons" data-testid="group-suggestions">
      {items.map((g) => (
        <li key={g.groupId} role="option" aria-selected={false}>
          {/* onMouseDown: picked before the search box loses focus and hides the list. */}
          <button type="button" onMouseDown={(e) => { e.preventDefault(); onPick(g.groupId); }} onClick={() => onPick(g.groupId)} data-testid="group-suggestion">
            <Layers size={13} />
            <span className="group-suggest-label">{g.label}</span>
            <span className="faint">{g.memberCount}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** The chain the columns read: the asset's deepest group (a Season before a stand-alone group of another type). */
const deepest = (a: AssetSummary): AssetGroupRef | null => a.groups?.[0] ?? null;

/** The top of the asset's chain: the Series. */
function seriesOf(a: AssetSummary): string | null {
  const g = a.groups?.find((x) => x.typeKey === 'series') ?? null;
  if (g) return g.title;
  const d = deepest(a);
  return d ? (d.path[0]?.title ?? d.title) : null;
}

/** The group below the top: the Season (its number in front when the title does not say it). */
function seasonOf(a: AssetSummary): string | null {
  const g = a.groups?.find((x) => x.typeKey === 'season') ?? a.groups?.find((x) => x.path.length > 0) ?? null;
  if (!g) return null;
  return g.groupNumber !== null && !g.title.includes(String(g.groupNumber)) ? `${g.groupNumber} · ${g.title}` : g.title;
}

const dash = <span className="faint">—</span>;

export function groupColumns(): Column[] {
  return [
    { id: 'group_series', label: 'Series', muted: true, render: (a) => seriesOf(a) ?? dash },
    { id: 'group_season', label: 'Season', muted: true, render: (a) => seasonOf(a) ?? dash },
    { id: 'group_episode', label: 'Episode no.', mono: true, render: (a) => { const n = deepest(a)?.number; return n !== null && n !== undefined ? n : dash; } },
  ];
}

export const GROUP_SORT_OPTION: { value: 'group_order'; label: string } | null = { value: 'group_order', label: 'Series order' };

type Sort = 'newest' | 'oldest' | 'title' | 'duration' | 'size' | 'group_order';
/**
 * The sort the Library uses (1.4.0 after the 1M cloud run): Series order only while a group filter is set (the API
 * refuses it without one, 422 SORT_NEEDS_GROUP: it orders every asset by a per-row lookup). A stored or saved Series
 * order without the filter falls back to the sort chosen before it (`previous`), else newest first.
 */
export function librarySort(stored: Sort | null | undefined, previous: Sort | null | undefined, group: string | null | undefined): Sort {
  if (stored === 'group_order' && !group) return previous && previous !== 'group_order' ? previous : 'newest';
  return stored ?? 'newest';
}

/** For G1's columns: an asset's group of a type key ('series', 'season'). */
export const groupOfType = (a: AssetSummary, typeKey: string) => a.groups?.find((g) => g.typeKey === typeKey) ?? null;
